import React, { useState, useEffect } from 'react';
import { 
  UserGroupIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  RefreshIcon,
  CheckCircleIcon,
  XCircleIcon,
  ShieldCheckIcon,
  UserIcon,
  KeyIcon
} from '@heroicons/react/outline';
import api from '../../services/api';
import { formatDate } from '../../utils/formatDate';
import { usePermissions } from '../../contexts/PermissionContext';
import PermissionGate from '../common/PermissionGate';

const AdminSettings = () => {
  const { hasPermission, isEmployee } = usePermissions();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    role: 'employee',
    permissions: {
      view_dashboard: true,
      manage_bookings: false,
      manage_users: false,
      view_analytics: false,
      manage_tracking: false,
      manage_rates: false,
      view_reports: false,
      manage_settings: false
    }
  });

  const rolePermissions = {
    admin: {
      view_dashboard: true,
      manage_bookings: true,
      manage_users: true,
      view_analytics: true,
      manage_tracking: true,
      manage_rates: true,
      view_reports: true,
      manage_settings: true
    },
    manager: {
      view_dashboard: true,
      manage_bookings: true,
      manage_users: false,
      view_analytics: true,
      manage_tracking: true,
      manage_rates: false,
      view_reports: true,
      manage_settings: false
    },
    employee: {
      view_dashboard: true,
      manage_bookings: true,
      manage_users: false,
      view_analytics: false,
      manage_tracking: true,
      manage_rates: false,
      view_reports: false,
      manage_settings: false
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      // First, try to fix any users with missing fields
      try {
        await api.post('/admin/fix-admin-users');
      } catch (fixError) {
        console.log('Fix users endpoint not available or failed:', fixError);
      }
      
      const response = await api.get('/admin/admin-users');
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setErrorMessage('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (role) => {
    setFormData({
      ...formData,
      role,
      permissions: rolePermissions[role]
    });
  };

  const handlePermissionToggle = (permission) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [permission]: !formData.permissions[permission]
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update user
        await api.put(`/admin/admin-users/${editingUser.id}`, formData);
        setSuccessMessage('User updated successfully');
      } else {
        // Create new user
        await api.post('/admin/admin-users', formData);
        setSuccessMessage('User created successfully');
      }
      
      setShowModal(false);
      setEditingUser(null);
      resetForm();
      fetchUsers(); // Reload users from API
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving user:', error);
      console.error('Error response:', error.response?.data);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to save user';
      setErrorMessage(errorMsg);
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      full_name: user.full_name,
      role: user.role,
      permissions: rolePermissions[user.role]
    });
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/admin/admin-users/${userId}`);
        setSuccessMessage('User deleted successfully');
        fetchUsers(); // Reload users from API
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (error) {
        console.error('Error deleting user:', error);
        setErrorMessage(error.response?.data?.detail || 'Failed to delete user');
        setTimeout(() => setErrorMessage(''), 5000);
      }
    }
  };

  const toggleUserStatus = async (userId) => {
    try {
      await api.put(`/admin/admin-users/${userId}/toggle-status`);
      fetchUsers(); // Reload users from API
    } catch (error) {
      console.error('Error toggling user status:', error);
      setErrorMessage(error.response?.data?.detail || 'Failed to update user status');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      full_name: '',
      role: 'employee',
      permissions: rolePermissions.employee
    });
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'manager': return 'bg-blue-100 text-blue-800';
      case 'employee': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            User Management
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage admin, manager, and employee accounts with role-based permissions
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            onClick={fetchUsers}
            className="mr-3 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <RefreshIcon className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <PermissionGate permission="create_user">
            <button
              onClick={() => {
                setEditingUser(null);
                resetForm();
                setShowModal(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Add User
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <CheckCircleIcon className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <XCircleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{errorMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Current User Permissions */}
      {isEmployee() && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <ShieldCheckIcon className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-blue-900">Your Permissions (Employee Role)</h3>
              <p className="text-sm text-blue-700 mt-1">As an employee, you have view-only access to this page.</p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <div className="flex items-center text-sm text-blue-800">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                  View Dashboard
                </div>
                <div className="flex items-center text-sm text-blue-800">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                  Manage Bookings
                </div>
                <div className="flex items-center text-sm text-blue-800">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                  Manage Tracking
                </div>
                <div className="flex items-center text-sm text-blue-800">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                  View Alerts
                </div>
                <div className="flex items-center text-sm text-red-600">
                  <XCircleIcon className="w-4 h-4 text-red-500 mr-2" />
                  Cannot Manage Users
                </div>
                <div className="flex items-center text-sm text-red-600">
                  <XCircleIcon className="w-4 h-4 text-red-500 mr-2" />
                  Cannot View Analytics
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            System Users ({users.length})
          </h3>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-orange-100 rounded-full flex items-center justify-center">
                          <UserIcon className="h-5 w-5 text-orange-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                          <div className="text-sm text-gray-500">@{user.username}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleUserStatus(user.id)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {user.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <PermissionGate permission="manage_system_users">
                          <button
                            onClick={() => handleEdit(user)}
                            className="text-orange-600 hover:text-orange-900"
                            title="Edit User"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="delete_user">
                          <button
                            onClick={() => handleDelete(user.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete User"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </PermissionGate>
                        {!hasPermission('manage_system_users') && (
                          <span className="text-xs text-gray-400">View Only</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Role Permissions Reference */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <ShieldCheckIcon className="w-5 h-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Role Permissions Reference</h3>
          </div>
          <p className="mt-1 text-sm text-gray-500">Default permissions for each role level</p>
        </div>
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(rolePermissions).map(([role, perms]) => (
              <div key={role} className="border border-gray-200 rounded-lg p-4">
                <h4 className={`text-sm font-semibold mb-3 px-2.5 py-0.5 rounded-full inline-block ${getRoleBadgeColor(role)}`}>
                  {role.toUpperCase()}
                </h4>
                <ul className="space-y-2 mt-3">
                  {Object.entries(perms).map(([perm, enabled]) => (
                    <li key={perm} className="flex items-center text-sm">
                      {enabled ? (
                        <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                      ) : (
                        <XCircleIcon className="w-4 h-4 text-gray-300 mr-2" />
                      )}
                      <span className={enabled ? 'text-gray-900' : 'text-gray-400'}>
                        {perm.replace(/_/g, ' ')}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-3xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Username</label>
                    <input
                      type="text"
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-500"
                      value={formData.username}
                      onChange={(e) => setFormData({...formData, username: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Full Name</label>
                    <input
                      type="text"
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-500"
                      value={formData.full_name}
                      onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-500"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Password {editingUser && '(leave blank to keep current)'}
                    </label>
                    <input
                      type="password"
                      required={!editingUser}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-500"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                    />
                  </div>
                </div>

                {/* Role Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                  <div className="grid grid-cols-3 gap-3">
                    {['admin', 'manager', 'employee'].map((role) => (
                      <button
                        key={role}
                        type="button"
                        onClick={() => handleRoleChange(role)}
                        className={`px-4 py-3 border-2 rounded-lg text-sm font-medium transition-colors ${
                          formData.role === role
                            ? 'border-orange-500 bg-orange-50 text-orange-700'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                        }`}
                      >
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Permissions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Permissions</label>
                  <div className="grid grid-cols-2 gap-3 bg-gray-50 p-4 rounded-lg">
                    {Object.entries(formData.permissions).map(([perm, enabled]) => (
                      <div key={perm} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={() => handlePermissionToggle(perm)}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 text-sm text-gray-700">
                          {perm.replace(/_/g, ' ')}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingUser(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-orange-600 hover:bg-orange-700"
                  >
                    {editingUser ? 'Update User' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;
